import { useCallback } from 'react';
import {
  NavigateFunction,
  NavigateOptions,
  useBeforeUnload,
  useBlocker,
  useNavigate,
} from 'react-router-dom';

let shouldBypassPrompt = false;

/**
 * Block router navigation or browser tab close.
 * @param message - e.g: "Changes have not been saved. Leaving can result in unsaved changes being lost."
 */

interface PromptProps {
  when: boolean;
  message: string;
}

export function Prompt({ when, message }: PromptProps) {
  useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if not active or in Cypress
    if (!when || 'Cypress' in window) return false;

    if (shouldBypassPrompt) return false;

    if (
      currentLocation.pathname === nextLocation.pathname &&
      currentLocation.search === nextLocation.search
    ) {
      return false;
    }

    // eslint-disable-next-line no-alert
    const isUserConfirmed = window.confirm(message);
    return !isUserConfirmed;
  });

  useBeforeUnload((event: BeforeUnloadEvent) => {
    if (!when || 'Cypress' in window) return;
    event.preventDefault();
    event.returnValue = message;
  });

  return null;
}

/**
 * Same as useNavigate, but the navigation is deferred to the next event loop
 * tick.
 *
 * It should be used in cases where <Prompt> is triggered unexpectedly after a
 * programmatic navigation due to stale `when` prop value.
 *
 * This is a temporary solution, and should be removed in favor of default
 * navigate function when the issue is resolved in a non-hacky way.
 */
export function useBypassNavigationPrompt(): NavigateFunction {
  const navigate = useNavigate();

  // Keep the identity stable like `useNavigate` does: callers list it in
  // effect deps (e.g. `useLocationParams`) expecting mount-only behavior.
  return useCallback<NavigateFunction>(
    (to, options?) => {
      bypassNavigationPrompt(() => {
        if (typeof to === 'number') {
          navigate(to);
        } else {
          navigate(to, options as NavigateOptions);
        }
      });
    },
    [navigate],
  );
}

export function bypassNavigationPrompt(callback: () => void) {
  shouldBypassPrompt = true;

  try {
    callback();
  } finally {
    shouldBypassPrompt = false;
  }
}
